import { Component, inject, CUSTOM_ELEMENTS_SCHEMA, effect } from "@angular/core";
import {
  GlossaryService,
  Glossary,
  Status,
} from "../services/admin/glossary.service";
import { AlphabetQuickSearch } from "../admin/components/alphabetQuickSearch/alphabetQuickSearch";
import { CloudWatchRumService } from '../services/cloudwatch-rum.service';
import { SettingsService } from '../services/user/settings.service';
import { SubscriptionsService } from '../services/user/subscriptions.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { isFeatureEnabledInUserSettings, getUserActiveProducts } from "../../utils";
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

export interface ResourceItem {
  name: string;
  url: string;
}

@Component({
  selector: "app-help",
  standalone: true,
  imports: [AlphabetQuickSearch],
  templateUrl: "./help.component.html",
  styleUrl: "./help.component.css",
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class HelpComponent {
  glossaryService: GlossaryService = inject(GlossaryService);
  private settingsService = inject(SettingsService);
  private subscriptionsService = inject(SubscriptionsService);
  private sanitizer = inject(DomSanitizer);
  response: string = "";
  searchQuery: string = "";
  faqs: Glossary[] = [];
  glossary: Glossary[] = [];
  resources: Glossary[] = [];
  filteredFaqs: Glossary[] = [];
  filteredGlossary: Glossary[] = [];
  filteredResources: Glossary[] = [];
  
  // Resource PDF viewer properties
  resourcePDFs: ResourceItem[] = [];
  filteredResourcePDFs: ResourceItem[] = [];
  selectedResourceUrl: SafeResourceUrl | null = null;
  selectedResourceName: string | null = null;
  showPDFViewer: boolean = false;

  // Feature flag signals based on user settings
  public isFAQEnabled = toSignal(
    this.settingsService.getUserSettings().pipe(
      map(settings => {
        const userActiveProducts = getUserActiveProducts();
        return isFeatureEnabledInUserSettings(settings, 'Help', 'FAQ', userActiveProducts);
      })
    ),
    { initialValue: true }
  );

  public isGlossaryEnabled = toSignal(
    this.settingsService.getUserSettings().pipe(
      map(settings => {
        const userActiveProducts = getUserActiveProducts();
        return isFeatureEnabledInUserSettings(settings, 'Help', 'Glossary', userActiveProducts);
      })
    ),
    { initialValue: true }
  );

  public isResourceEnabled = toSignal(
    this.settingsService.getUserSettings().pipe(
      map(settings => {
        const userActiveProducts = getUserActiveProducts();
        return isFeatureEnabledInUserSettings(settings, 'Help', 'Resource', userActiveProducts);
      })
    ),
    { initialValue: true }
  );

  // Compute filtered tabs based on feature flags
  public get filteredTabItems() {
    const allTabs = [
      { name: "FAQs", selected: false, param: "faq" },
      { name: "Glossary", selected: false, param: "glossary" },
      { name: "Resources", selected: false, param: "resource" },
    ];

    return allTabs.filter(tab => {
      if (tab.param === "faq") return this.isFAQEnabled();
      if (tab.param === "glossary") return this.isGlossaryEnabled();
      if (tab.param === "resource") return this.isResourceEnabled();
      return true;
    })
    .map(tab => ({
      ...tab,
      selected: tab.param === this.selectedView
    }));
  }

  constructor(private cloudWatchRumService: CloudWatchRumService) {
    this.fetchGlossaryData();
    this.loadResourcePDFs();
    

    // Set default selected view to first available tab
    effect(() => {
      const availableTabs = this.filteredTabItems;
      if (availableTabs.length > 0 && !this.selectedView) {
        this.selectedView = availableTabs[0].param;
      }
      // If current view is no longer available, switch to first available
      else if (availableTabs.length > 0 && !availableTabs.some(tab => tab.param === this.selectedView)) {
        this.selectedView = availableTabs[0].param;
      }
    }); 
  }
  selectedView: string | undefined = "faq";
  tabItems = [
    { name: "FAQs", selected: false, param: "faq" },
    { name: "Glossary", selected: false, param: "glossary" },
    { name: "Resources", selected: false, param: "resource" },
  ];

  fetchGlossaryData() {
    this.glossaryService.getGlossary().subscribe({
      next: (response) => {
        if (response) {
          this.faqs = this.filteredFaqs = response.filter(
            (item: Glossary) =>
              item.glossaryType === "FAQ" && item.status === Status.ACTIVE
          ).sort((a: Glossary, b: Glossary) => a.glossaryName > b.glossaryName ? 1 : -1);
          this.glossary = this.filteredGlossary = response.filter(
            (item: Glossary) =>
              item.glossaryType === "TERM" && item.status === Status.ACTIVE
          ).sort((a: Glossary, b: Glossary) => a.glossaryName > b.glossaryName ? 1 : -1);
          this.resources = this.filteredResources = response.filter(
            (item: Glossary) =>
              item.glossaryType === "RESOURCE" && item.status === Status.ACTIVE
          ).sort((a: Glossary, b: Glossary) => a.glossaryName > b.glossaryName ? 1 : -1);
        } else {
          console.warn("No data in API response.");
        }
      },
      error: (error) => {
        console.error("Error fetching glossary data:", error);
      },
    });
  }

  loadResourcePDFs() {
    this.subscriptionsService.getUserGuide().subscribe({
      next: (resourceMap) => {
        this.resourcePDFs = this.filteredResourcePDFs = Object.entries(resourceMap).map(([name, url]) => ({
          name,
          url
        }));
      },
      error: (error) => {
        console.error('Error loading resource PDFs:', error);
      }
    });
  }

  selectResource(resource: ResourceItem) {
    this.selectedResourceName = resource.name;
    const urlWithNoToolbar = resource.url + '#toolbar=0';
    this.selectedResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(urlWithNoToolbar);
    this.showPDFViewer = true;
    this.cloudWatchRumService.addCustomEvent('resourceSelected', { resourceName: resource.name });
  }

  backToResourceList() {
    this.showPDFViewer = false;
    this.selectedResourceUrl = null;
    this.selectedResourceName = null;
  }

  onTabItemSelected(event: Event) {
    const payload = (
      event as CustomEvent<{
        param: string;
      }>
    ).detail;

    this.selectedView = payload.param;
  }

  onInputBlur(event: Event) {
    const payload = (
      event as CustomEvent<{
        uuid: string;
        value: string;
        initialValue: string;
      }>
    ).detail;
  }

  onInputFocused(event: Event) {
    const payload = (
      event as CustomEvent<{
        uuid: string;
        value: string;
        initialValue: string;
      }>
    ).detail;

  }

  onInputChanged(event: Event) {
    const payload = (
      event as CustomEvent<{
        uuid: string;
        selected: boolean;
        value: string;
      }>
    ).detail;
    this.searchQuery = payload.value;
  }

  onSearch() {
    this.filterGlossaryItems();
    this.cloudWatchRumService.addCustomEvent('helpSearch', { searchQuery: this.searchQuery });
  }

  filterGlossaryItems() {
    const query = this.searchQuery.trim().toLowerCase();
    const regex = new RegExp(`\\b${query}\\b`, "g");
    if (query === "") {
      this.filteredFaqs = this.faqs;
      this.filteredGlossary = this.glossary;
      this.filteredResources = this.resources;
      this.filteredResourcePDFs = this.resourcePDFs;
      return;
    }

    this.filteredFaqs = this.faqs.filter(
      (item) =>
        item.glossaryName.toLowerCase().match(regex) ||
        item.description.toLowerCase().match(regex)
    ).sort((a: Glossary, b: Glossary) => a.glossaryName > b.glossaryName ? 1 : -1);
    this.filteredGlossary = this.glossary.filter(
      (item) =>
        item.glossaryName.toLowerCase().match(regex) ||
        item.description.toLowerCase().match(regex)
    ).sort((a: Glossary, b: Glossary) => a.glossaryName > b.glossaryName ? 1 : -1);
    this.filteredResources = this.resources.filter(
      (item) =>
        item.glossaryName.toLowerCase().match(regex) ||
        item.description.toLowerCase().match(regex)
    ).sort((a: Glossary, b: Glossary) => a.glossaryName > b.glossaryName ? 1 : -1);
    
    // Filter resource PDFs
    this.filteredResourcePDFs = this.resourcePDFs.filter(
      (item) => item.name.toLowerCase().includes(query)
    );
  }

  filterByLetter(letter: string): void {
    if (this.selectedView === "glossary") {
      const lowercaseLetter = letter.toLowerCase();
      if (letter === "#") {
        this.filteredGlossary = this.glossary.filter(
          (term) => /^[^a-zA-Z]/.test(term.glossaryName)
        ).sort((a: Glossary, b: Glossary) => a.glossaryName > b.glossaryName ? 1 : -1);
        return;
      }
      if (letter === "All") {
        this.filteredGlossary = this.glossary;
        return;
      }
      this.filteredGlossary = this.glossary.filter((term) =>
        term.glossaryName.toLowerCase().startsWith(lowercaseLetter)
      ).sort((a: Glossary, b: Glossary) => a.glossaryName > b.glossaryName ? 1 : -1);
    } else if (this.selectedView === "resource") {
      const lowercaseLetter = letter.toLowerCase();
      if (letter === "#") {
        this.filteredResources = this.resources.filter(
          (term) => /^[^a-zA-Z]/.test(term.glossaryName)
        ).sort((a: Glossary, b: Glossary) => a.glossaryName > b.glossaryName ? 1 : -1);
        return;
      }
      if (letter === "All") {
        this.filteredResources = this.resources;
        return;
      }
      this.filteredResources = this.resources.filter((term) =>
        term.glossaryName.toLowerCase().startsWith(lowercaseLetter)
      ).sort((a: Glossary, b: Glossary) => a.glossaryName > b.glossaryName ? 1 : -1);
    }
  }
}
